<script setup lang="ts">
import { FilterX, ShieldCheck, SlidersHorizontal } from "@lucide/vue";
import { refDebounced } from "@vueuse/core";
import { computed, h, ref } from "vue";
import { usePrincipals } from "@/api/admin";
import { useAuditEvents, type AuditFilter } from "@/api/audit";
import type { AuditEvent } from "@/api/contracts";
import AuditEventSheet from "@/components/audit/AuditEventSheet.vue";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SearchInput from "@/components/common/SearchInput.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import type { Column } from "@/lib/table";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Audit", requires: { audit: true }, layout: "full" } });

const { can } = useSession();
const q = useRouteQuery("q");
const principalId = useRouteQuery("user");
const category = useRouteQuery("category");
const tool = useRouteQuery("tool");
const datasource = useRouteQuery("datasource");
const status = useRouteQuery("status");
const from = useRouteQuery("from");
const to = useRouteQuery("to");
const qDebounced = refDebounced(q, 300);
const toolDebounced = refDebounced(tool, 400);
const dsDebounced = refDebounced(datasource, 400);

const principals = usePrincipals();
const canPickUser = computed(() => can("admin.users") && !principals.error.value);
const userOptions = computed(() => (principals.data.value ?? []).map((p) => ({ value: p.id, label: p.displayName || p.username, hint: p.email ?? undefined })));

const filter = computed<AuditFilter>(() => ({
  q: qDebounced.value || undefined,
  principalId: principalId.value || undefined,
  category: category.value || undefined,
  tool: toolDebounced.value || undefined,
  datasource: dsDebounced.value || undefined,
  status: (status.value || undefined) as AuditFilter["status"],
  from: from.value ? new Date(from.value).toISOString() : undefined,
  to: to.value ? new Date(to.value).toISOString() : undefined,
  limit: 100,
}));
const events = useAuditEvents(filter);
const items = computed(() => events.data.value?.pages.flatMap((p) => p.items) ?? []);
const fields = [q, principalId, category, tool, datasource, status, from, to];
const anyFilter = computed(() => fields.some((r) => r.value));
/** Filters besides the text search (the count on the phone "Filters" button). */
const otherFilters = computed(() => [principalId, category, tool, datasource, status, from, to].filter((r) => r.value).length);
// Phones: the filter fields fold away behind one button, so the events stay above the fold.
const filtersOpen = ref(otherFilters.value > 0);

function clearAll() {
  for (const r of fields) r.value = "";
}

const CATEGORIES = ["tool", "write", "exec", "admin", "auth", "knowledge", "read"];
const STATUSES = [
  { value: "success", label: "Success" },
  { value: "error", label: "Error" },
  { value: "denied", label: "Denied" },
];

const selected = ref<AuditEvent | null>(null);
const open = ref(false);
function show(e: AuditEvent) {
  selected.value = e;
  open.value = true;
}

/** "mcp · claude.ai", "web" (not "web · web"). */
const viaLabel = (e: AuditEvent) => [...new Set([e.via, e.client].filter(Boolean))].join(" · ") || "—";

const columns: Column<AuditEvent>[] = [
  {
    id: "when",
    header: "When",
    accessorFn: (e) => e.occurredAt,
    meta: { mobile: "subtitle" },
    cell: ({ row }) => h(RelativeTime, { value: row.original.occurredAt, class: "whitespace-nowrap text-sm" }),
  },
  { id: "user", header: "User", accessorFn: (e) => e.actorName ?? "", cell: ({ row }) => h("span", { class: "font-medium whitespace-nowrap" }, row.original.actorName ?? "—") },
  {
    id: "what",
    header: "What",
    accessorFn: (e) => `${e.summary ?? ""} ${e.action} ${e.tool ?? ""}`,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "whitespace-normal sm:min-w-56" }, [
        h("p", { class: "line-clamp-2 text-sm [overflow-wrap:anywhere]" }, row.original.summary || row.original.action),
        h("p", { class: "mt-0.5 font-mono text-xs font-normal text-muted-foreground [overflow-wrap:anywhere]" }, [
          row.original.category,
          row.original.tool ? ` · ${row.original.tool}` : "",
          row.original.action !== row.original.tool ? ` · ${row.original.action}` : "",
        ]),
      ]),
  },
  {
    id: "target",
    header: "Target",
    accessorFn: (e) => e.target ?? e.datasource ?? "",
    cell: ({ row }) => h("span", { class: "block max-w-64 truncate text-sm", title: row.original.target ?? row.original.datasource ?? undefined }, row.original.target ?? row.original.datasource ?? "—"),
  },
  {
    id: "via",
    header: "Via",
    accessorFn: (e) => viaLabel(e),
    meta: { mobile: "hidden" },
    cell: ({ row }) => h("span", { class: "text-xs whitespace-nowrap text-muted-foreground" }, viaLabel(row.original)),
  },
  { id: "status", header: "Status", accessorFn: (e) => e.status, meta: { mobile: "badge" }, cell: ({ row }) => h(StatusBadge, { status: row.original.status }) },
];
</script>

<template>
  <div>
    <PageHeader :title="t.audit.title" :description="t.audit.description">
      <template v-if="anyFilter" #actions>
        <Button variant="ghost" @click="clearAll"><FilterX /> {{ t.common.clear }}</Button>
      </template>
    </PageHeader>

    <!-- Two rows on large screens (search + who/what, then where/when); phones fold the fields behind "Filters". -->
    <section aria-label="Filters" class="surface mb-5 grid gap-3 p-3 sm:p-4 lg:grid-cols-[minmax(16rem,2fr)_repeat(3,minmax(0,1fr))]">
      <div class="flex min-w-0 items-end gap-2">
        <div class="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label for="audit-q" class="max-lg:sr-only">{{ t.audit.text }}</Label>
          <SearchInput id="audit-q" v-model="q" placeholder="Summary, target, error or user name" :label="t.audit.text" />
        </div>
        <Button variant="outline" class="shrink-0 lg:hidden" :aria-expanded="filtersOpen" aria-controls="audit-filters" @click="filtersOpen = !filtersOpen">
          <SlidersHorizontal /> Filters
          <span v-if="otherFilters" class="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground tabular-nums">
            {{ otherFilters }}
          </span>
        </Button>
      </div>
      <div id="audit-filters" :class="cn('gap-3 sm:grid-cols-2 lg:contents', filtersOpen ? 'grid' : 'hidden')">
        <div v-if="canPickUser" class="flex min-w-0 flex-col gap-1.5">
          <Label>{{ t.audit.user }}</Label>
          <SimpleSelect v-model="principalId" :options="userOptions" allow-empty :empty-label="t.common.all" placeholder="Everyone" :aria-label="t.audit.user" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Label>{{ t.audit.category }}</Label>
          <SimpleSelect v-model="category" :options="CATEGORIES" allow-empty :empty-label="t.common.all" placeholder="All" :aria-label="t.audit.category" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Label>{{ t.audit.status }}</Label>
          <SimpleSelect v-model="status" :options="STATUSES" allow-empty :empty-label="t.common.all" placeholder="All" :aria-label="t.audit.status" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Label for="audit-tool">{{ t.audit.tool }}</Label>
          <Input id="audit-tool" v-model="tool" placeholder="e.g. mysql_query" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Label for="audit-ds">{{ t.audit.datasource }}</Label>
          <Input id="audit-ds" v-model="datasource" placeholder="type/name" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Label for="audit-from">{{ t.audit.from }}</Label>
          <Input id="audit-from" v-model="from" type="datetime-local" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <Label for="audit-to">{{ t.audit.to }}</Label>
          <Input id="audit-to" v-model="to" type="datetime-local" />
        </div>
      </div>
    </section>

    <ListSkeleton v-if="events.isPending.value" :rows="8" />
    <ErrorState v-else-if="events.error.value" :error="events.error.value" @retry="events.refetch()" />
    <EmptyState v-else-if="items.length === 0" :icon="ShieldCheck" :title="t.audit.empty" :description="anyFilter ? 'Loosen a filter or clear them all.' : undefined">
      <Button v-if="anyFilter" variant="outline" @click="clearAll">{{ t.common.clear }}</Button>
    </EmptyState>
    <template v-else>
      <DataTable :data="items" :columns="columns" row-clickable :page-size="500" :row-key="(e) => String(e.id)" caption="Audit events" @row-click="show" />
      <div v-if="events.hasNextPage.value" class="mt-4 flex justify-center">
        <Button variant="outline" :loading="events.isFetchingNextPage.value" @click="events.fetchNextPage()">{{ t.common.loadMore }}</Button>
      </div>
    </template>
    <AuditEventSheet v-model:open="open" :event="selected" />
  </div>
</template>
